/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { Constants } from '../../core/constants';
import { DeviceModel } from '../../core/device';
import { PCState } from '../../core/enums';
import { ErrorTypes, LiveTvError } from '../../core/error';
import { ServicesClientImpl } from '../../core/interactors';
import { Log, VuLogger } from '../../core/logging';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { QoEConnectionModeType } from '../../core/logging/qoe/QoEConnectionModeType';
import { ScreenControllerTypes } from '../../core/navigation';
import { Keys } from '../../core/navigation/Keys';
import { MouseOperations } from '../../core/navigation/MouseOperations';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import {
    PlaybackRestrictionType,
    PlayerViewMode,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import {
    LiveTvGeneralErrorMapper,
    LiveTvLocationErrorMapper,
    LiveTvPlaybackErrorMapper,
    PlaybackErrorDisplay,
} from '../../core/videoplayer/error';
import { IMetadata, IPlayerStatus } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { getRecordingsStatus } from '../../store/recordings/recordings.reducers';
import { resetPlaybackParamsVideoplayer } from '../../store/videoplayer/videoplayer.reducers';
import { onKeyBackVideoPlayerUi } from '../../store/videoplayerui/videoplayeruinavigation.reducers';
import { Mousetrap } from '../common';
import { ImageLoader } from '../ImageLoader';
import { ImageLoaderTypes } from '../ImageLoader/ImageLoaderTypes';

import './VideoPlayer.scss';

const VideoPlayer: FunctionComponent = (): JSX.Element => {
    const ID = Constants.VIDEO_PLAYER_UI_ID;
    const TAG = 'Videoplayer';

    let mHasError = false;
    let mIsRestricted = false;
    const unmuted = DeviceModel.isWebOsPlatform() || DeviceModel.isTizenPlatform();

    const dispatch = useTivoDispatch();
    const isVideoPlayerActive = useRef(true);
    const uiContainerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef(null);

    // useSelectors
    const { eventType, identity, key, value } = useTivoSelector(
        (state: RootState) => state.inputFactory,
    );
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const pcState = useTivoSelector((state: RootState) => state.parentalControls.pcState);
    const screenController = useTivoSelector((state: RootState) => state.screens.screenController);

    // useStates
    const [isAppInForeground, setIsAppInForeground] = useState(true);
    const [isBlackSurfaceActive, setIsBlackSurfaceActive] = useState(false);
    const [isMediaPauseIconVisible, setIsMediaPauseIconVisible] = useState(false);
    const [isVideoPaused, setIsVideoPaused] = useState(false);
    const [videoWindowIcon, setVideoWindowIcon] = useState(
        ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON,
    );

    VideoPlaybackController.getInstance().setIsAntiburnActive(isAntiburnOsdActive);

    const handleLocationRestriction = () => {
        VideoPlaybackController.getInstance().stop();
        setIsBlackSurfaceActive(true);
        mIsRestricted = true;
    };

    const handleLocationUnreachable = () => {
        if (VideoPlaybackController.getInstance().isGracePeriodFinished()) {
            handleLocationRestriction();
        } else {
            setIsBlackSurfaceActive(false);
        }
    };

    const handleNetworkConnection = () => {
        setIsBlackSurfaceActive(!isAntiburnOsdActive);
        mIsRestricted = false;
    };

    const handleNetworkDisconnection = () => {
        setIsBlackSurfaceActive(true);
        mIsRestricted = true;
        VideoPlaybackController.getInstance().pause(false);
    };

    const handleOnProgramBoundary = () => {
        loggingsOnProgramBoundary();
    };

    const handlePlaybackRestriction = (type: PlaybackRestrictionType) => {
        const playerInstance = VideoPlaybackController.getInstance();
        const playerViewMode = playerInstance.getPlayerViewMode();
        let errorObj: LiveTvError = {} as LiveTvError;
        Log.d(TAG, 'handlePlaybackRestriction type is', PlaybackRestrictionType[type]);
        switch (type) {
            case PlaybackRestrictionType.APP_BACKGROUND:
                setIsAppInForeground(false);
                break;
            case PlaybackRestrictionType.APP_FOREGROUND:
                setIsAppInForeground(true);
                break;
            case PlaybackRestrictionType.ADULT: {
                Log.i(TAG, 'Adult Content');
                const services = new ServicesClientImpl();
                const metadata: IMetadata = VideoPlaybackController.getInstance().getMetadata();
                const { airingOffers, offerPreview } = metadata;
                const isOfferRatedAdult =
                    airingOffers?.[0]?.parentalControls?.isAdult ??
                    offerPreview?.contentPreview?.isAdult ??
                    false;

                const isOfferConsideredAdult =
                    isOfferRatedAdult && services.isPCHideAdultContentEnabled();

                if (isOfferConsideredAdult) {
                    errorObj = new LiveTvGeneralErrorMapper().getErrorByCode('');
                } else {
                    const ratingName =
                        services.getRestrictedRatingName(metadata) ?? Constants.UNKNOWN;

                    errorObj = new LiveTvGeneralErrorMapper().getErrorByCode('', '', ratingName);
                }

                VideoPlaybackController.getInstance().mute(true);
                setIsBlackSurfaceActive(true);
                break;
            }
            case PlaybackRestrictionType.BLACKLISTED:
                handleLocationRestriction();
                errorObj = new LiveTvLocationErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_IN_BLACKLIST_OSD_ERROR_C138,
                );
                break;
            case PlaybackRestrictionType.DEVICE_RESTRICTED:
                errorObj = new LiveTvGeneralErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414,
                );
                break;
            case PlaybackRestrictionType.INHOME_LOCATION_ERROR:
                handleLocationRestriction();
                errorObj = new LiveTvLocationErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_LOCATION_RESTRICTION_OSD_ERROR_CODE_V418,
                );
                break;
            case PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE:
                handleLocationUnreachable();
                if (VideoPlaybackController.getInstance().isGracePeriodFinished()) {
                    errorObj = new LiveTvPlaybackErrorMapper().getErrorByCode(
                        ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718,
                    );
                }
                break;
            case PlaybackRestrictionType.NETWORK_DISCONNECTED:
                handleNetworkDisconnection();
                errorObj = new LiveTvGeneralErrorMapper().getErrorByCode(
                    ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE,
                );
                break;
            case PlaybackRestrictionType.OUT_OF_HOME:
                handleLocationRestriction();
                errorObj = new LiveTvLocationErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404,
                );
                break;
            case PlaybackRestrictionType.OUT_OF_REGION:
                logDeviceRestrictionChange(type);
                handleLocationRestriction();
                errorObj = new LiveTvLocationErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405,
                );
                break;
            case PlaybackRestrictionType.UNSUBSCRIBED:
                {
                    mIsRestricted = true;
                    const channelName =
                        VideoPlaybackController.getInstance().getPlayable()?.channel?.name;
                    errorObj = new LiveTvGeneralErrorMapper().getErrorByCode(
                        ErrorTypes.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_ERROR_CODE_V56,
                        channelName,
                    );
                }
                break;
            case PlaybackRestrictionType.NETWORK_CONNECTED:
                handleNetworkConnection();
                break;
            case PlaybackRestrictionType.NO_RESTRICTION:
                mIsRestricted = false;
                logDeviceRestrictionChange(type);
                setIsBlackSurfaceActive(false);
                VideoPlaybackController.getInstance().mute(false);
                break;
            default:
                return;
        }
        VuLogger.logVideoPlayerRestrictionNotificationEvent(type);
        if (Object.keys(errorObj).length) {
            QoEAppLogger.logVideoPlayerRestrictionNotificationEvent(
                type,
                errorObj,
                playerInstance.getPlayerStats(),
                playerInstance.getPlayerCurrentTime(),
                playerInstance.getSeekRange(),
            );
            if (playerViewMode === PlayerViewMode.FullScreen)
                PlaybackErrorDisplay.getInstance().displayError(
                    errorObj,
                    VideoPlaybackController.getInstance().getPlayable()?.playbackContentType,
                );
        }
    };

    // TODO: [KST-5305] Fix this with error handling
    const handlePlayerError = (): void => {
        mHasError = true;
        VideoPlaybackController.getInstance().stop();
    };

    const handleVideoStateChange = (status: IPlayerStatus): void => {
        switch (status.state) {
            case VideoPlayerStates.PAUSE:
                setIsVideoPaused(true);
                mHasError = false;
                break;
            case VideoPlayerStates.PLAYING:
                mHasError = false;
                setIsVideoPaused(false);
                setIsMediaPauseIconVisible(false);
                break;
            default:
        }
    };

    const handlePlayerViewModeChange = (playerViewMode: PlayerViewMode) => {
        Log.d(TAG, 'handlePlayerViewModeChange playerVewMode: ', playerViewMode);
        switch (playerViewMode) {
            case PlayerViewMode.FullScreen:
                setIsMediaPauseIconVisible(false);
                break;
            case PlayerViewMode.VideoWindow:
                onPlayerMinimized();
                break;
            default:
        }
    };

    const handleVisibilityChange = () => {
        const playerInstance = VideoPlaybackController.getInstance();
        const playerViewMode = playerInstance.getPlayerViewMode();
        if (!isAppInForeground) {
            QoEAppLogger.logVisibilityBackground(
                playerInstance.getPlayerStats(),
                playerInstance.getPlayerCurrentTime(),
                playerInstance.getSeekRange(),
            );
            stopVideoPlayback();
            return;
        }
        if (playerViewMode === PlayerViewMode.FullScreen)
            dispatch(resetPlaybackParamsVideoplayer());

        if (
            !DeviceModel.getAppSettings().isVideoWindowEnabled() &&
            playerViewMode === PlayerViewMode.VideoWindow
        )
            return;

        startVideoPlayback(true); // Skip video window check for handleVisibilityChange
    };

    // TODO: We should move this function to QoE logger.
    const logDeviceRestrictionChange = (type: PlaybackRestrictionType) => {
        if (type !== PlaybackRestrictionType.OUT_OF_REGION) {
            QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.LOCAL);
            Diagnostics.info(TAG, 'logDeviceRestrictionChange: inRegion');
            Log.i(TAG, 'logDeviceRestrictionChange: inRegion');
        } else {
            QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.AWAY);
            Diagnostics.info(TAG, 'logDeviceRestrictionChange: outOfRegion');
            Log.i(TAG, 'logDeviceRestrictionChange: outOfRegion');
        }
    };

    const loggingsOnProgramBoundary = () => {
        const playerInstance = VideoPlaybackController.getInstance();
        QoEAppLogger.logProgramEnded(
            playerInstance.getPlayerStats(),
            playerInstance.getPlayerCurrentTime(),
            playerInstance.getSeekRange(),
        );
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.END_OF_CONTENT);
        QoEAppLogger.logWatchContentStart(
            playerInstance.getPlayable()?.playbackContentType,
            playerInstance.getPlayable()?.channel,
            playerInstance.getMetadata()?.airingOffers,
        );
    };

    const onKeyPlayPauseVideoPlayer = () => {
        togglePlaybackState();
    };

    const onMouseClickVideoPlayer = () => {
        togglePlaybackState();
    };

    const onMouseHoverVideoPlayer = () => {
        if (!isVideoPaused) setVideoWindowIcon(ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON);
        else setVideoWindowIcon(ImageLoaderTypes.MEDIA_PAUSE_ICON);
    };

    const onPlayerMinimized = () => {
        const isPlaying = VideoPlaybackController.getInstance().isPlaying();
        if (!isPlaying && !mHasError && !mIsRestricted) setIsMediaPauseIconVisible(true);
    };

    const startVideoPlayback = async (skipVideoWindowCheck = false) => {
        try {
            if (skipVideoWindowCheck || DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                const lastTunedChannelNumber =
                    DeviceModel.getAppSettings().getLastTunedChannelNumber();
                const playable =
                    await new ServicesClientImpl().getLastPlayedOrFallbackToFirstByChannelNumber(
                        lastTunedChannelNumber,
                    );
                VideoPlaybackController.getInstance().startPlayback(playable);
            }
        } catch (error) {
            Log.e(TAG, 'startVideoPlayback', error);
        }
    };

    const stopVideoPlayback = () => {
        if (!VideoPlaybackController.getInstance().isLive()) {
            VideoPlaybackController.getInstance().updateLastViewedPosition();
            PlaybackDataModelImpl.getInstance().resetPlaybackData();
        }
        VideoPlaybackController.getInstance().stop();
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VISIBILITY_BEHIND);
    };

    const togglePlaybackState = () => {
        const canPause =
            !isVideoPaused &&
            VideoPlaybackController.getInstance().getPlayerViewMode() !==
                PlayerViewMode.FullScreen &&
            VideoPlaybackController.getInstance().isNetworkConnected() &&
            VideoPlaybackController.getInstance().trickModeRestrictions()?.allowPause;
        if (canPause) {
            setIsMediaPauseIconVisible(true);
            setIsVideoPaused(true);
            setVideoWindowIcon(ImageLoaderTypes.MEDIA_PAUSE_ICON);
            VideoPlaybackController.getInstance().pause(true);
            return;
        }
        if (
            isVideoPaused &&
            VideoPlaybackController.getInstance().isNetworkConnected() &&
            VideoPlaybackController.getInstance().getPlayerViewMode() !== PlayerViewMode.FullScreen
        ) {
            setIsMediaPauseIconVisible(false);
            setIsVideoPaused(false);
            setVideoWindowIcon(ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON);
            VideoPlaybackController.getInstance().resume();
        }
    };

    const handleUIOrchestratorStateChange = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.RECORDING_DELETED:
                if (VideoPlaybackController.getInstance().isNdvr()) {
                    dispatch(onKeyBackVideoPlayerUi());
                    startVideoPlayback();
                }
                dispatch(getRecordingsStatus());
                break;
            case UIOrchestratorState.RECORDING_CREATED:
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.ONE_PASS_MODIFIED:
                dispatch(getRecordingsStatus());
                break;
            default:
        }
    };

    useEffect(() => {
        VideoPlaybackController.getInstance()
            .getRestrictionListener()
            .subscribe(handlePlaybackRestriction);
        VideoPlaybackController.getInstance()
            .getPlayerViewListener()
            .subscribe(handlePlayerViewModeChange);
        VideoPlaybackController.getInstance()
            .getPlayerStateChangeListener()
            .subscribe(handleVideoStateChange);
        VideoPlaybackController.getInstance()
            .getPlaybackErrorListener()
            .subscribe(handlePlayerError);
        VideoPlaybackController.getInstance()
            .getProgramBoundaryListener()
            .subscribe(handleOnProgramBoundary);
        UIOrchestrator.stateChangeListener.subscribe(handleUIOrchestratorStateChange);
        return () => {
            VideoPlaybackController.getInstance()
                .getRestrictionListener()
                .cancel(handlePlaybackRestriction);
            VideoPlaybackController.getInstance()
                .getPlayerViewListener()
                .cancel(handlePlayerViewModeChange);
            VideoPlaybackController.getInstance()
                .getPlayerStateChangeListener()
                .cancel(handleVideoStateChange);
            VideoPlaybackController.getInstance()
                .getPlaybackErrorListener()
                .cancel(handlePlayerError);
            VideoPlaybackController.getInstance()
                .getProgramBoundaryListener()
                .cancel(handleOnProgramBoundary);
            UIOrchestrator.stateChangeListener.cancel(handleUIOrchestratorStateChange);
        };
    }, []);

    useEffect(() => {
        if (DeviceModel.getAppSettings().isVideoWindowEnabled() && isAppInForeground) {
            if (isAntiburnOsdActive) {
                stopVideoPlayback();
            } else {
                startVideoPlayback(true); // Skip video window check for antiburnOsd
            }
        }

        if (DeviceModel.getAppSettings().isVideoWindowEnabled()) {
            uiContainerRef?.current?.classList.toggle('hidden', isAntiburnOsdActive);
        }
    }, [isAntiburnOsdActive]);

    useEffect(() => {
        if (isVideoPlayerActive.current && videoRef.current && uiContainerRef.current) {
            VideoPlaybackController.getInstance().configurePlayer(
                videoRef.current,
                uiContainerRef.current,
            );
        }
        return () => {
            isVideoPlayerActive.current = false;
        };
    }, [uiContainerRef, videoRef]);

    useEffect(() => {
        if (value !== ID) return;
        if (
            screenController === ScreenControllerTypes.SPLASH_SCREEN ||
            screenController === ScreenControllerTypes.SIGN_IN_SCREEN
        )
            return;
        switch (eventType) {
            case MouseOperations.MOUSE_CLICK:
                onMouseClickVideoPlayer();
                break;

            case MouseOperations.MOUSE_OUT:
                setIsMediaPauseIconVisible(false);
                break;

            case MouseOperations.MOUSE_OVER:
                setIsMediaPauseIconVisible(true);
                onMouseHoverVideoPlayer();
                break;

            default:
        }
    }, [eventType, value]);

    useEffect(() => {
        switch (key) {
            /**
             * Both pause and play/pause buttons currently share the same functionality. If
             * future changes require different functionalities, separate functions may be needed.
             */
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PAUSE:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_PAUSE:
            case Keys.KEYBOARD_KEY_PAUSE:
            case Keys.KEYBOARD_KEY_PLAY_PAUSE:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY_PAUSE:
                onKeyPlayPauseVideoPlayer();
                break;
            default:
        }
    }, [identity]);

    /**
     * This useEffect handles changes in visibility state:
     * - When visibility is ON_PAUSE, playback is stopped. If Socu mode is enabled, the last viewed
     *   position is updated, and Socu data is reset.
     * - When visibility is ON_RESUME and a preserved playable exists, a new playable is fetched
     *   from the preserved channel number and live playback starts.
     */

    useEffect(() => {
        handleVisibilityChange();
    }, [isAppInForeground]);

    /** This useEffect will restart the playback whenever the Parental Controls state changes
     *  to re-evaluate the playback restrictions.
     */
    useEffect(() => {
        const isPCStateChanged =
            pcState === PCState.Unlocked || pcState === PCState.Off || pcState === PCState.Locked;
        if (isPCStateChanged) {
            VideoPlaybackController.getInstance().restorePlayback();
        }
    }, [pcState]);

    return (
        <React.Fragment>
            <div className="video-container minimize hidden" ref={uiContainerRef}>
                <Mousetrap value={ID}>
                    <div className={`black-surface ${!isBlackSurfaceActive ? 'hidden' : ''}`}></div>
                    <video ref={videoRef} muted={!unmuted}></video>
                    {isMediaPauseIconVisible ? <ImageLoader imageType={videoWindowIcon} /> : null}
                </Mousetrap>
            </div>
        </React.Fragment>
    );
};

export default VideoPlayer;
